/**
 * KnowledgePage — browse, create, and approve Knowledge Articles.
 *
 * Knowledge Articles are the "Approved Knowledge" tier of the Ticket Learning Loop:
 * Ticket → Resolution Note → Knowledge Candidate → Human Approval → Knowledge Article
 *
 * Approved articles are automatically included in AI analysis prompts
 * with higher trust than raw ticket history.
 */
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { knowledgeApi, knowledgeGapsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { parseUtc } from "../utils/utcDate";

// Backend maximum for GET /api/knowledge/ (default is only 100). The list is sorted newest-first,
// so a smaller cap silently hides the oldest articles - exactly the stale/unused ones the
// "needs review" and "unused" views exist to surface.
const KNOWLEDGE_LIST_LIMIT = 500;

const fmtDate = (iso) =>
  iso ? new Intl.DateTimeFormat("he-IL", { dateStyle: "short" }).format(parseUtc(iso)) : "—";

function stalenessBadge(updatedAt) {
  if (!updatedAt) return null;
  const days = Math.floor((Date.now() - parseUtc(updatedAt).getTime()) / 86400000);
  if (days < 30) return null;
  if (days < 90)  return { label: `עודכן לפני ${days}י`, cls: "text-gray-400" };
  if (days < 180) return { label: `עודכן לפני ${days}י`, cls: "text-amber-500" };
  return { label: `עודכן לפני ${days}י ⚠`, cls: "text-red-500 font-medium" };
}

const EMPTY_FORM = { title: "", problem_pattern: "", solution: "", tags: "" };

export function ArticleModal({ article, prefill, saving = false, onSave, onCancel }) {
  const [form, setForm] = useState(
    article
      ? { title: article.title, problem_pattern: article.problem_pattern, solution: article.solution, tags: article.tags || "" }
      : prefill
        ? { ...EMPTY_FORM, ...prefill }
        : EMPTY_FORM
  );

  // No Escape handler previously -- a keyboard user had to tab to the "ביטול" button.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
      <div role="dialog" aria-modal="true" aria-labelledby="article-modal-title" className="bg-white rounded-xl shadow-xl border p-6 w-[36rem] max-h-[90vh] overflow-y-auto space-y-4">
        <h3 id="article-modal-title" className="text-base font-semibold text-gray-800">
          {article ? "עריכת מאמר" : "מאמר ידע חדש"}
        </h3>

        <div>
          <label className="block text-xs text-gray-500 mb-1">כותרת *</label>
          <input
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="לדוגמה: שחזור גישה לחשבון Office 365 לאחר נעילה"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">תבנית הבעיה *</label>
          <textarea
            rows={3}
            value={form.problem_pattern}
            onChange={(e) => setForm((f) => ({ ...f, problem_pattern: e.target.value }))}
            placeholder="תאר את הסימפטומים ותנאי הבעיה — כך ה-AI יזהה קריאות דומות"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">פתרון *</label>
          <textarea
            rows={5}
            value={form.solution}
            onChange={(e) => setForm((f) => ({ ...f, solution: e.target.value }))}
            placeholder="פרט את שלבי הפתרון המאושרים..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">תגים (מופרדים בפסיקים)</label>
          <input
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            placeholder="לדוגמה: Office365, כניסה, סיסמה"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.title.trim() || !form.problem_pattern.trim() || !form.solution.trim()}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            שמור
          </button>
          <button type="button" onClick={onCancel} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}

const GAP_STATUS_TOAST = {
  ignored: "הפער הוסתר בהצלחה",
  acknowledged: "הפער סומן כמוכר",
  resolved: "הפער סומן כסגור",
  open: "הפער שוחזר לפתוח",
};

export default function KnowledgePage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [approvedOnly, setApprovedOnly] = useState(() => searchParams.get("approved") === "true");
  const [sortBy, setSortBy] = useState(() => searchParams.get("sort") === "effective" ? "effective" : "recent");
  const [unusedOnly, setUnusedOnly] = useState(() => searchParams.get("unused") === "true");
  const [showModal, setShowModal] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [prefillForm, setPrefillForm] = useState(null);
  const [needsReview, setNeedsReview] = useState(false);
  const [gapStatusFilter, setGapStatusFilter] = useState("open");

  // Auto-open new article modal when navigated from Knowledge Candidates panel
  useEffect(() => {
    const kw = searchParams.get("keyword");
    if (kw) {
      setPrefillForm({ title: kw, problem_pattern: kw, solution: "", tags: "" });
      setShowModal(true);
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete("keyword");
        return next;
      }, { replace: true });
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const next = new URLSearchParams();
    if (search.trim()) next.set("search", search.trim());
    if (approvedOnly) next.set("approved", "true");
    if (unusedOnly) next.set("unused", "true");
    if (sortBy !== "recent") next.set("sort", sortBy);
    setSearchParams(next, { replace: true });
  }, [search, approvedOnly, sortBy, unusedOnly, setSearchParams]);

  const { data: articles = [], isLoading, isError: articlesFailed } = useQuery({
    queryKey: ["knowledge", search, approvedOnly, sortBy, unusedOnly],
    queryFn: () => unusedOnly
      ? knowledgeApi.listUnused({ search: search || undefined, limit: KNOWLEDGE_LIST_LIMIT })
      : knowledgeApi.list({
          search: search || undefined,
          approved_only: approvedOnly,
          sort_by: sortBy,
          limit: KNOWLEDGE_LIST_LIMIT,
        }),
  });

  const { data: recurringGaps = [] } = useQuery({
    queryKey: ["knowledge-gaps-needs-article"],
    queryFn: knowledgeGapsApi.listNeedsArticle,
    staleTime: 120000,
  });

  const createMutation = useMutation({
    mutationFn: knowledgeApi.create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["knowledge"] }); setShowModal(false); addNotification("מאמר ידע נוצר בהצלחה", "success"); },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת מאמר ידע", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => knowledgeApi.update(id, data),
    onSuccess: (updated) => {
      const lostApproval = editArticle?.is_approved && updated && !updated.is_approved;
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      // Edit sets BOTH editArticle and showModal: clearing only the article left the dialog open as a "new article" form still
      // holding the edited values, and its next Save created a duplicate.
      setShowModal(false);
      setEditArticle(null);
      addNotification(lostApproval ? "מאמר ידע עודכן וחזר לטיוטה — נדרש אישור מחדש" : "מאמר ידע עודכן", lostApproval ? "info" : "success");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון מאמר ידע", "error"),
  });

  const approveMutation = useMutation({
    mutationFn: knowledgeApi.approve,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["knowledge"] }); addNotification("מאמר ידע אושר ✓", "success"); },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באישור מאמר", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: knowledgeApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["knowledge"] }); addNotification("מאמר ידע נמחק", "info"); },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת מאמר", "error"),
  });

  const gapArticleMutation = useMutation({
    mutationFn: knowledgeGapsApi.createArticle,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-needs-article"] });
      // PlatformHealthPage's "X open" badge is its own separate query (a different first key
      // element, so the prefix above does not match it) -- same hazard as this page's own
      // gapStatusMutation below, just reachable across a page navigation instead of within one.
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-open-count"] });
      const msg = data?.created === false
        ? `מאמר קיים נמצא: "${data.title}"`
        : `טיוטת מאמר נוצרה: "${data?.title}"`;
      addNotification(msg, "success");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת מאמר מהפער", "error"),
  });

  const { data: allGaps = [], isError: gapsFailed } = useQuery({
    queryKey: ["knowledge-gaps", gapStatusFilter],
    queryFn: () => knowledgeGapsApi.list({ status_filter: gapStatusFilter }),
    staleTime: 60000,
  });

  const gapStatusMutation = useMutation({
    mutationFn: ({ id, status }) => knowledgeGapsApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["knowledge-gaps"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-needs-article"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-open-count"] });
      const msg = GAP_STATUS_TOAST[variables.status];
      if (msg) addNotification(msg, "info");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון סטטוס הפער", "error"),
  });

  const handleSave = (form) => {
    if (createMutation.isPending || updateMutation.isPending) return;     // a double click created two articles
    if (editArticle) {
      updateMutation.mutate({ id: editArticle.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const approvedCount = articles.filter((a) => a.is_approved).length;
  const visibleArticles = needsReview
    ? articles.filter((a) => {
        if (a.rejection_count >= 3) return true;
        if (!a.updated_at) return false;
        const days = Math.floor((Date.now() - parseUtc(a.updated_at).getTime()) / 86400000);
        return days >= 90;
      })
    : articles;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">בסיס ידע מאושר</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            מאמרים מאושרים כלולים אוטומטית בהקשר של ניתוח AI — ביטחון גבוה יותר מהיסטוריה גולמית.
          </p>
        </div>
        <button
          onClick={() => { setEditArticle(null); setPrefillForm(null); setShowModal(true); }}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          + מאמר חדש
        </button>
      </div>

      {/* Stats bar */}
      <div className="flex gap-3 flex-wrap text-sm">
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-green-700">
          <span className="font-semibold">{approvedCount}</span> מאושרים
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
          <span className="font-semibold">{articles.length - approvedCount}</span> טיוטות
        </div>
      </div>

      {articles.length >= KNOWLEDGE_LIST_LIMIT && (
        <p role="status" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
          מוצגים {KNOWLEDGE_LIST_LIMIT} המאמרים העדכניים ביותר בלבד. הספירות והסינון "טעון סקירה" מתייחסים לרשימה המוצגת — השתמשו בחיפוש כדי למצוא מאמרים ישנים יותר.
        </p>
      )}

      {recurringGaps.length > 0 && (
        <section className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-amber-800">פערי ידע חוזרים</h2>
              <p className="text-xs text-amber-700 mt-0.5">דפוסים שנצפו לפחות 5 פעמים ועדיין זקוקים למאמר</p>
            </div>
            <span className="text-xs text-amber-700">{recurringGaps.length} מועמדים</span>
          </div>
          <div className="space-y-2">
            {recurringGaps.slice(0, 5).map((gap) => (
              <div key={gap.id} className="bg-white border border-amber-100 rounded-lg px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 font-medium">{gap.gap_text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {gap.occurrence_count} הופעות · נראה לאחרונה {fmtDate(gap.last_seen_at)}
                      {gap.source_tickets?.length ? ` · ${gap.source_tickets.length} קריאות מקור` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => gapArticleMutation.mutate(gap.id)}
                    disabled={gapArticleMutation.isPending && gapArticleMutation.variables === gap.id}
                    className="shrink-0 text-xs text-amber-800 border border-amber-300 rounded px-2 py-1 hover:bg-amber-100 disabled:opacity-50"
                  >
                    צור טיוטה
                  </button>
                </div>
                {gap.source_tickets?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 items-center">
                    <span className="text-[10px] text-amber-700">מקור:</span>
                    {gap.source_tickets.slice(0, 3).map((ticket) => (
                      <Link
                        key={ticket.id}
                        to={`/tickets/${ticket.id}`}
                        className="text-[11px] text-brand-600 hover:underline bg-gray-50 border rounded px-1.5 py-0.5"
                      >
                        #{ticket.ticket_number}
                      </Link>
                    ))}
                    {gap.source_tickets.length > 3 && (
                      <span className="text-[10px] text-gray-400">+{gap.source_tickets.length - 3} נוספות</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש..."
          className="border rounded-lg px-3 py-2 text-sm w-64"
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={approvedOnly}
            onChange={(e) => setApprovedOnly(e.target.checked)}
            className="rounded"
          />
          מאושרים בלבד
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={unusedOnly}
            onChange={(e) => setUnusedOnly(e.target.checked)}
            className="rounded"
          />
          לא נוצל
        </label>
        <label className="flex items-center gap-1.5 text-sm text-red-600 cursor-pointer" title="מאמרים עם ≥3 דחיות או לא עודכנו 90+ יום">
          <input
            type="checkbox"
            checked={needsReview}
            onChange={(e) => setNeedsReview(e.target.checked)}
            className="rounded"
          />
          טעון סקירה
        </label>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          <button
            onClick={() => setSortBy("recent")}
            className={`px-3 py-1.5 ${sortBy === "recent" ? "bg-brand-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            חדש
          </button>
          <button
            onClick={() => setSortBy("effective")}
            className={`px-3 py-1.5 border-r border-gray-200 ${sortBy === "effective" ? "bg-brand-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            נפוץ ביותר
          </button>
        </div>
      </div>

      {/* Articles list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : articlesFailed && visibleArticles.length === 0 ? (
        <div role="alert" className="py-12 text-center text-red-600 text-sm">לא ניתן לטעון את מאמרי הידע</div>
      ) : visibleArticles.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          {needsReview ? "אין מאמרים הטעונים סקירה." : "אין מאמרים עדיין. ניתן ליצור מאמר ידע חדש או לקדם מועמד מלוח הבקרה."}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleArticles.map((a) => (
            <div key={a.id} className="bg-white border rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-medium text-gray-800 text-sm">{a.title}</h3>
                    {a.is_approved ? (
                      <span className="text-xs bg-green-100 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
                        ✓ מאושר על ידי {a.approved_by?.split("@")[0]} · {fmtDate(a.approved_at)}
                      </span>
                    ) : (
                      <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                        טיוטה
                      </span>
                    )}
                    {a.tags && (
                      <span className="text-xs text-gray-400">{a.tags}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 mb-1.5">
                    <span className="font-medium text-gray-600">תבנית: </span>
                    {a.problem_pattern.slice(0, 120)}{a.problem_pattern.length > 120 ? "..." : ""}
                  </p>
                  <p className="text-xs text-gray-700 bg-green-50 border border-green-100 rounded px-2 py-1.5">
                    <span className="font-medium text-green-700">פתרון: </span>
                    {a.solution.slice(0, 200)}{a.solution.length > 200 ? "..." : ""}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 flex flex-wrap items-center gap-2">
                    {a.usage_count > 0 ? (
                      <>
                        <span>שימושים: {a.usage_count}</span>
                        {a.last_used_at && <span>· שימוש אחרון: {fmtDate(a.last_used_at)}</span>}
                      </>
                    ) : (
                      <span className="text-amber-500">לא נעשה שימוש עדיין</span>
                    )}
                    {a.rejection_count > 0 && (
                      <span
                        className={`font-medium ${a.rejection_count >= 3 ? "text-red-500" : "text-orange-400"}`}
                        title="מספר הפעמים שהניתוח שהשתמש במאמר זה נדחה על ידי טכנאי"
                      >
                        · דחיות: {a.rejection_count}
                      </span>
                    )}
                    {a.company_id ? (
                      <span className="bg-indigo-50 border border-indigo-200 text-indigo-600 rounded px-1.5 py-0.5 text-[10px]">חברה ספציפית</span>
                    ) : (
                      <span className="bg-gray-50 border border-gray-200 text-gray-400 rounded px-1.5 py-0.5 text-[10px]">גלובלי</span>
                    )}
                    {(() => { const s = stalenessBadge(a.updated_at); return s ? <span className={s.cls}>{s.label}</span> : null; })()}
                  </p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {!a.is_approved && (
                    <button
                      onClick={() => approveMutation.mutate(a.id)}
                      disabled={approveMutation.isPending && approveMutation.variables === a.id}
                      className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium"
                    >
                      אשר
                    </button>
                  )}
                  <button
                    onClick={() => { setEditArticle(a); setPrefillForm(null); setShowModal(true); }}
                    className="text-xs border border-gray-300 hover:border-gray-400 px-3 py-1.5 rounded-lg text-gray-600"
                  >
                    עריכה
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm("למחוק מאמר זה?"))
                        deleteMutation.mutate(a.id);
                    }}
                    disabled={deleteMutation.isPending && deleteMutation.variables === a.id}
                    className="text-xs text-red-500 hover:text-red-600 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    מחק
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Knowledge Gaps admin panel */}
      <section className="border border-gray-200 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">פערי ידע</h2>
            <p className="text-xs text-gray-500 mt-0.5">דפוסים שזוהו ב-AI שעדיין אין עליהם מאמר מאושר</p>
          </div>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            {[["open", "פתוחים"], ["acknowledged", "מוכרים"], ["resolved", "סגורים"], ["ignored", "מוסתרים"]].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setGapStatusFilter(val)}
                className={`px-3 py-1.5 border-r last:border-r-0 border-gray-200 ${
                  gapStatusFilter === val ? "bg-brand-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {label}
                {val === gapStatusFilter && allGaps.length > 0 && (
                  <span className="ml-1 bg-white/30 text-current rounded-full px-1">{allGaps.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {gapsFailed ? (
          // GET /knowledge-gaps is admin-only while this page is shown to other roles: a 403 (or any failure) is not "no gaps".
          <div role="alert" className="py-8 text-center text-red-600 text-sm">לא ניתן לטעון את פערי הידע (ייתכן שנדרשת הרשאת מנהל)</div>
        ) : allGaps.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">אין פערי ידע בסטטוס זה</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {allGaps.map((gap) => (
              <div key={gap.id} className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {gap.category && (
                      <span className="text-[10px] bg-indigo-50 border border-indigo-200 text-indigo-600 rounded px-1.5 py-0.5">
                        {gap.category}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {gap.occurrence_count} הופעות · נראה לאחרונה {fmtDate(gap.last_seen_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800">{gap.gap_text}</p>
                  {gap.source_tickets?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5 items-center">
                      <span className="text-[10px] text-gray-500">מקור:</span>
                      {gap.source_tickets.slice(0, 4).map((t) => (
                        <Link
                          key={t.id}
                          to={`/tickets/${t.id}`}
                          className="text-[10px] text-brand-600 hover:underline bg-gray-50 border rounded px-1.5 py-0.5"
                        >
                          #{t.ticket_number}
                        </Link>
                      ))}
                      {gap.source_tickets.length > 4 && (
                        <span className="text-[10px] text-gray-400">+{gap.source_tickets.length - 4} נוספות</span>
                      )}
                    </div>
                  )}
                  {gapStatusFilter === "ignored" && gap.ignored_by && (
                    <p className="text-[10px] text-gray-400 mt-1">הוסתר על ידי {gap.ignored_by}</p>
                  )}
                </div>
                {gapStatusFilter === "ignored" || gapStatusFilter === "resolved" ? (
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => gapStatusMutation.mutate({ id: gap.id, status: "open" })}
                      disabled={gapStatusMutation.isPending && gapStatusMutation.variables?.id === gap.id}
                      className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-50 text-gray-600"
                    >
                      {gapStatusFilter === "resolved" ? "פתח מחדש" : "שחזר לפתוח"}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                    {gapStatusFilter === "open" && (
                      <button
                        type="button"
                        onClick={() => gapStatusMutation.mutate({ id: gap.id, status: "acknowledged" })}
                        disabled={gapStatusMutation.isPending && gapStatusMutation.variables?.id === gap.id}
                        className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-50 text-gray-600"
                      >
                        אשר
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => gapStatusMutation.mutate({ id: gap.id, status: "resolved" })}
                      disabled={gapStatusMutation.isPending && gapStatusMutation.variables?.id === gap.id}
                      className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-50 text-gray-600"
                    >
                      סגור
                    </button>
                    <button
                      type="button"
                      onClick={() => gapArticleMutation.mutate(gap.id)}
                      disabled={gapArticleMutation.isPending && gapArticleMutation.variables === gap.id}
                      className="text-xs bg-brand-600 hover:bg-brand-700 text-white rounded px-2 py-1 disabled:opacity-50"
                    >
                      צור מאמר
                    </button>
                    <button
                      type="button"
                      onClick={() => gapStatusMutation.mutate({ id: gap.id, status: "ignored" })}
                      disabled={gapStatusMutation.isPending && gapStatusMutation.variables?.id === gap.id}
                      className="text-xs border border-gray-200 rounded px-2 py-1 hover:bg-red-50 hover:border-red-200 hover:text-red-600 disabled:opacity-50 text-gray-400"
                    >
                      התעלם
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {(showModal || editArticle) && (
        <ArticleModal
          article={editArticle}
          prefill={!editArticle ? prefillForm : undefined}
          saving={createMutation.isPending || updateMutation.isPending}
          onSave={handleSave}
          onCancel={() => { setShowModal(false); setEditArticle(null); setPrefillForm(null); }}
        />
      )}
    </div>
  );
}
